import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, patchTrip, patchRound } from '../lib/api';
import { getTripAccess } from '../lib/tripAccess';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Plus, Trash, PencilSimple, Lock } from '@phosphor-icons/react';
import AddPlayerForm from '../components/AddPlayerForm';
import { toast } from 'sonner';
import { roundHasScores } from '../lib/formatHelpers';
import { formatHandicapIndex, formatPlayingHandicap, assignDivision } from '../golf';

export default function PlayerManagement() {
  const { code } = useParams();
  const navigate = useNavigate();
  const cacheKey = `trip_${code}`;
  const [trip, setTrip] = useState(() => {
    try { const c = sessionStorage.getItem(cacheKey); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [loading, setLoading] = useState(!trip);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [selectedRoundIdx, setSelectedRoundIdx] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }

  const load = useCallback(async () => {
    try {
      const res = await getTrip(code);
      setTrip(res.data);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(res.data)); } catch {}
    } catch {} finally { setLoading(false); }
  }, [code, cacheKey]);
  useEffect(() => { load(); }, [load]);

  const savePlayers = async (updatedPlayers) => {
    // Auto-assign division_id for new/unassigned players when divisions enabled
    if (trip?.divisions_enabled && trip?.divisions?.length > 0) {
      updatedPlayers = updatedPlayers.map(p => {
        if (!p.division_locked && !p.division_id) {
          const autoDiv = assignDivision(p, trip.divisions);
          if (autoDiv) return { ...p, division_id: autoDiv, division_source: 'auto' };
        }
        return p;
      });
    }
    // Optimistic update — render immediately, reconcile on response
    const prevTrip = trip;
    const optimistic = prevTrip ? { ...prevTrip, players: updatedPlayers } : prevTrip;
    setTrip(optimistic);
    try { sessionStorage.setItem(cacheKey, JSON.stringify(optimistic)); } catch {}
    setSaving(true);
    try {
      const res = await patchTrip(code, { players: updatedPlayers });
      setTrip(res.data);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(res.data)); } catch {}
    } catch (e) {
      // Rollback on failure
      setTrip(prevTrip);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(prevTrip)); } catch {}
      if (e.response?.status === 402 && e.response?.data?.code === 'cap_exceeded') {
        toast.error(`You've hit your players cap for this trip.`);
        const hasPromo = e.response?.data?.has_promo;
        if (hasPromo) {
          navigate(`/trip/${code}/upgrade?kind=players&delta_players=1`);
        } else {
          navigate(`/trip/${code}/extra-capacity?kind=players&delta_players=1`);
        }
      } else {
        toast.error('Failed to save player — please try again.');
      }
    } finally { setSaving(false); }
  };

  const isOrganiser = (() => {
    const access = getTripAccess(code);
    return access?.role === 'organiser' || access?.legacy;
  })();

  const handleRemove = (player) => {
    setDeleteTarget(player);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    savePlayers((trip?.players || []).filter(p => p.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const anyScoringStarted = trip?.any_scoring_started || false;

  const openAdd = () => {
    // Pre-check lifetime cap before opening modal
    if (!trip?.legacy_unlimited && trip?.max_players) {
      const used = trip.players_created ?? trip.players?.length ?? 0;
      if (used >= trip.max_players) {
        toast.error(`You've hit your players cap for this trip.`);
        if (trip.promo_code) {
          navigate(`/trip/${code}/upgrade?kind=players&delta_players=1`);
        } else {
          navigate(`/trip/${code}/extra-capacity?kind=players&delta_players=1`);
        }
        return;
      }
    }
    setEditTarget(null); setDialogOpen(true);
  };
  const openEdit = (p) => { setEditTarget(p); setDialogOpen(true); };
  const closeDialog = () => { setDialogOpen(false); setEditTarget(null); };

  const players = trip?.players || [];
  const rounds = trip?.rounds || [];
  const roundInFocus = rounds[selectedRoundIdx] || null;

  const handleRoundUpdated = (updatedTrip) => {
    setTrip(updatedTrip);
    try { sessionStorage.setItem(cacheKey, JSON.stringify(updatedTrip)); } catch {}
  };

  // Check if a player has scores on any round
  const playerHasScores = (playerId) => {
    return (trip?.rounds || []).some(rd => {
      const hs = (rd.hole_strokes || {})[playerId];
      return Array.isArray(hs) && hs.some(v => v > 0);
    });
  };

  if (loading) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="player-management">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(`/trip/${code}`)} className="text-white p-1" data-testid="back-btn"><ArrowLeft className="w-5 h-5" weight="bold" /></button>
        <div className="flex-1"><h1 className="text-xl text-white font-bold">Players</h1><p className="text-xs text-[#B8C7C0]">{trip?.name}</p></div>
        <span className="bg-[#E8E5DB] text-[#1B4332] text-sm font-bold px-2.5 py-1 rounded-full">{players.length}</span>
      </div>

      <div className="px-4 py-6 max-w-lg mx-auto">
        {players.length === 0 ? (
          <Card className="p-8 bg-white border-[#E1E6E4] rounded-xl text-center animate-fade-in-up">
            <p className="text-[#455561]">No players added yet</p>
            <p className="text-xs text-[#B8C7C0] mt-1">Add at least 8 to begin</p>
          </Card>
        ) : (
          <div className="space-y-2 stagger-children">
            {[...players].sort((a, b) => {
              const va = a.handicap_mode === 'ph' && a.manual_ph != null ? a.manual_ph : a.whs_index;
              const vb = b.handicap_mode === 'ph' && b.manual_ph != null ? b.manual_ph : b.whs_index;
              return va - vb;
            }).map(p => (
              <Card key={p.id} className="p-0 bg-white border-[#E1E6E4] rounded-xl overflow-hidden" data-testid={`player-row-${p.id}`}>
                <div className="flex items-center min-h-[56px] px-4">
                  <div className="w-9 h-9 rounded-full bg-[#E8E5DB] flex items-center justify-center text-[#1B4332] font-bold text-sm mr-3 flex-shrink-0">
                    {p.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1D242B] text-sm truncate">{p.name}</p>
                  </div>
                  {p.handicap_mode === 'ph' ? (
                    <span className="text-sm font-bold text-[#1B4332] mr-3 tabular-nums">
                      PH {formatPlayingHandicap(p.manual_ph)} <span className="text-[9px] text-[#455561] font-normal">(manual)</span>
                    </span>
                  ) : (
                    <span className="text-sm font-bold text-[#1B4332] mr-3 tabular-nums">{formatHandicapIndex(p.whs_index)}</span>
                  )}
                  {trip?.divisions_enabled && (() => {
                    const div = (trip?.divisions || []).find(d => d.id === p.division_id);
                    return div ? <span className="text-[9px] text-[#8A9BA3] font-semibold mr-2">{div.name}</span> : null;
                  })()}
                  {isOrganiser ? (
                    <button onClick={() => openEdit(p)} className="p-2 text-[#B8C7C0] hover:text-[#1B4332] transition-colors" data-testid={`edit-player-${p.id}`}>
                      <PencilSimple className="w-4 h-4" weight="bold" />
                    </button>
                  ) : (
                    <span className="p-2 text-[#B8C7C0] opacity-40" data-testid={`edit-player-${p.id}`}>
                      <PencilSimple className="w-4 h-4" weight="bold" />
                    </span>
                  )}
                  {isOrganiser ? (
                    playerHasScores(p.id) ? (
                      <button onClick={() => toast.error('Master password required — player has recorded scores.')}
                        className="p-2 text-[#B8C7C0] opacity-40 cursor-not-allowed" data-testid={`remove-player-${p.id}`}>
                        <Lock className="w-4 h-4" weight="bold" />
                      </button>
                    ) : (
                      <button onClick={() => handleRemove(p)} className="p-2 text-[#B8C7C0] hover:text-[#D32F2F] transition-colors" data-testid={`remove-player-${p.id}`}>
                        <Trash className="w-4 h-4" weight="bold" />
                      </button>
                    )
                  ) : (
                    <span className="p-2 text-[#B8C7C0] opacity-40" data-testid={`remove-player-${p.id}`}>
                      <Lock className="w-4 h-4" weight="bold" />
                    </span>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* FAB — organiser only */}
        {isOrganiser && (
        <div className="fixed bottom-6 right-6 z-20">
          <Button onClick={openAdd} className="w-14 h-14 rounded-full bg-[#1B4332] hover:bg-[#163B2B] shadow-lg" data-testid="add-player-fab">
            <Plus className="w-6 h-6 text-white" weight="bold" />
          </Button>
        </div>
        )}
      </div>

      {/* Add/Edit Dialog — renders the shared AddPlayerForm (fields only, no list) */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="sm:max-w-sm rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editTarget ? 'Edit Player' : 'Add Player'}</DialogTitle></DialogHeader>
          {/* Round selector for group assignment — Add mode only */}
          {!editTarget && rounds.length > 0 && (
            <div className="flex gap-1.5 flex-wrap" data-testid="round-selector-pills">
              {rounds.map((r, i) => (
                <button key={r.id} type="button" onClick={() => setSelectedRoundIdx(i)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${i === selectedRoundIdx ? 'bg-[#1B4332] text-white' : 'bg-[#F4F6F5] text-[#455561] hover:bg-[#E1E6E4]'}`}
                  data-testid={`round-pill-${i}`}>
                  {r.label || `Round ${i + 1}`}
                </button>
              ))}
            </div>
          )}
          <AddPlayerForm
            key={editTarget?.id || 'add'}
            players={players}
            onSavePlayers={savePlayers}
            saving={saving}
            showList={false}
            initialEditPlayer={editTarget}
            onActionComplete={closeDialog}
            roundInFocus={roundInFocus}
            tripCode={code}
            onRoundUpdated={handleRoundUpdated}
            isOrganiser={isOrganiser}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-xs rounded-2xl" data-testid="delete-player-dialog">
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#455561]">
            {anyScoringStarted
              ? "They'll be removed from the event. Their place still counts towards your paid players."
              : "They'll be removed from the event and their place will be freed."}
          </p>
          <DialogFooter className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}
              className="flex-1 h-10 rounded-lg border-[#E1E6E4]" data-testid="cancel-delete-player">
              Cancel
            </Button>
            <Button onClick={confirmDelete}
              className="flex-1 h-10 rounded-lg bg-[#D32F2F] hover:bg-[#B71C1C] text-white border-0"
              data-testid="confirm-delete-player">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
